import React, { useState, useRef, useEffect } from 'react';
import { showSuccess, showError, showWarning } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

function FluxogramaModal({ isOpen, onClose, empreendimentoId, onSuccess, currentData }) {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Limpa o modal ao abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setSelectedImage(null);
      setSelectedVideo(null);
      setPreviewImage(null);
      setDescricao('');
      setProgress(0);
    }
  }, [isOpen]);

  const handleImageChange = (file) => {
    setSelectedImage(file);
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // 🛠️ FUNÇÃO DE UPLOAD (NUNCA defina Content-Type manualmente aqui!)
  const uploadFile = async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`
        // O navegador define o Content-Type: multipart/form-data automaticamente
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erro no upload de ${file.name}`);
    }
    const data = await response.json();
    return data.url;
  };

  const handleSave = async () => {
    // Validações
    if (!descricao.trim()) {
      return showWarning("Atenção", "Por favor, descreva o motivo desta atualização para o histórico.");
    }
    if (!selectedImage && !selectedVideo) {
      return showWarning("Atenção", "Selecione pelo menos um novo arquivo (Imagem ou Vídeo).");
    }

    setLoading(true);
    setProgress(10);

    try {
      const token = localStorage.getItem('token');
      
      // Mantém URLs atuais se não houver novo upload
      let finalImageUrl = currentData?.fluxograma_url;
      let finalVideoUrl = currentData?.fluxograma_video_url;

      // 1. Upload de Imagem
      if (selectedImage) {
        setProgress(30);
        finalImageUrl = await uploadFile(selectedImage);
      }

      // 2. Upload de Vídeo
      if (selectedVideo) {
        setProgress(60);
        finalVideoUrl = await uploadFile(selectedVideo);
      }

      setProgress(85);

      // 3. Montagem do Payload Exatamente como o server.js espera
      const payload = {
        fluxograma_url: finalImageUrl,
        fluxograma_video_url: finalVideoUrl,
        fluxograma_descricao: descricao // Este campo ativa o INSERT no histórico no servidor
      };

      const updateRes = await fetch(`${API_BASE_URL}/empreendimentos/${empreendimentoId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (updateRes.ok) {
        setProgress(100);
        await showSuccess("Sucesso!", "Arquivos atualizados e registrados no histórico.");
        onSuccess();
        onClose();
      } else {
        const errData = await updateRes.json();
        throw new Error(errData.message || "Erro ao salvar dados no banco.");
      }
    } catch (error) {
      console.error("Erro no processo:", error);
      showError("Falha na Atualização", error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col border border-white/20">
        
        {/* Header */}
        <div className="bg-slate-50 px-8 py-6 border-b border-slate-100 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              <span className="text-2xl">📐</span> Fluxograma & Vídeo
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1 ml-1">Atualização técnica de processo</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50 hover:text-red-500 text-slate-300 transition-all text-2xl">&times;</button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Upload Imagem */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Imagem / Desenho</label>
              <div 
                onClick={() => imageInputRef.current.click()} 
                className={`h-44 border-2 border-dashed rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden group ${previewImage ? 'border-sabesp-cyan bg-cyan-50/20' : 'border-slate-200 hover:border-sabesp-cyan bg-slate-50'}`}
              >
                <input type="file" ref={imageInputRef} className="hidden" accept="image/*" onChange={(e) => e.target.files[0] && handleImageChange(e.target.files[0])} />
                
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="text-center">
                    <div className="text-3xl mb-2">🖼️</div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-tighter">Substituir Imagem</p>
                  </div>
                )}
                {previewImage && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="bg-white px-4 py-1.5 rounded-full text-[10px] font-black text-sabesp-cyan shadow-xl">TROCAR</span>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Vídeo */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Vídeo Explicativo</label>
              <div 
                onClick={() => videoInputRef.current.click()} 
                className={`h-44 border-2 border-dashed rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all group ${selectedVideo ? 'border-purple-400 bg-purple-50' : 'border-slate-200 hover:border-purple-400 bg-slate-50'}`}
              >
                <input type="file" ref={videoInputRef} className="hidden" accept="video/mp4" onChange={(e) => e.target.files[0] && setSelectedVideo(e.target.files[0])} />
                <div className="text-center p-4">
                  <div className={`text-3xl mb-2 transition-transform ${selectedVideo ? 'scale-110' : 'group-hover:scale-110'}`}>
                    {selectedVideo ? '✅' : '📽️'}
                  </div>
                  <p className={`text-xs font-bold uppercase tracking-tighter ${selectedVideo ? 'text-purple-600' : 'text-slate-500'}`}>
                    {selectedVideo ? 'Vídeo Selecionado' : 'Adicionar Vídeo'}
                  </p>
                  {selectedVideo && <p className="text-[9px] text-slate-400 mt-1 truncate max-w-[150px] mx-auto italic">{selectedVideo.name}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Descrição do Histórico */}
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase ml-1 tracking-wider">O que mudou nesta versão? *</label>
            <textarea 
              value={descricao} 
              onChange={(e) => setDescricao(e.target.value)} 
              placeholder="Descreva as alterações técnicas para o registro histórico..." 
              className="w-full p-5 bg-slate-50 border-2 border-slate-100 rounded-[1.5rem] text-sm focus:bg-white focus:border-sabesp-cyan outline-none transition-all h-24 resize-none"
            />
          </div>

          {/* Barra de Progresso */}
          {loading && (
            <div className="space-y-2">
              <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <span>Processando arquivos pesados...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-sabesp-cyan transition-all duration-500" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-8 py-6 border-t border-slate-100 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-6 py-3 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-slate-600 transition-colors">Cancelar</button>
          <button 
            onClick={handleSave} 
            disabled={loading} 
            className="px-10 py-3 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl shadow-xl hover:bg-sabesp-blue transition-all disabled:opacity-50 flex items-center gap-3"
          >
            {loading ? 'Salvando...' : '🚀 Publicar Versão'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FluxogramaModal;